/**
 * <hidden-ticket-popup> — ticket-type chooser that stands between a "Buy Tickets"
 * CTA and the Easy Appointment Booking hand-off.
 *
 * Why it exists: EAB sets availability per *event*, not per ticket type, so a single
 * event carrying Weekday/Weekend/Student lets a customer book a Weekend ticket on a
 * Tuesday. The approved fix is three separate events — one ticket type each — and a
 * button can only bind to one event, so the customer picks the type first and lands
 * in the right event's calendar (Asana 1217809247481091).
 * Spec: docs/specs/screen-ticket-selection.md
 *
 * Opening is delegated from the document, so a trigger can live anywhere — the fixed
 * header, the sticky tab bar, or in-page — without knowing where the dialog is.
 * Two ways in:
 *   1. any element carrying [data-hidden-ticket-popup-open] (what hidden-button emits)
 *   2. a `hidden:ticket-popup:open` event on document (for app/3rd-party code)
 *
 * Native <dialog>.showModal() is doing the heavy lifting: focus trap, Esc, inertness
 * of the page behind, and ::backdrop. Scroll lock takes two halves: core dialog.js
 * watches any <dialog scroll-lock> and sets html[scroll-lock] { overflow: hidden },
 * and #setScrollLocked pauses Lenis, which scrolls from JS and ignores that.
 */

const OPEN_EVENT = 'hidden:ticket-popup:open';
const OPEN_SELECTOR = '[data-hidden-ticket-popup-open]';
const CLOSE_SELECTOR = '[data-hidden-ticket-popup-close]';

/** Longest we'll wait for the close animation before forcing the dialog shut. */
const CLOSE_ANIMATION_FALLBACK_MS = 400;

class HiddenTicketPopup extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {((event: MouseEvent) => void) | null} */
  #onDocumentClick = null;

  /** @type {(() => void) | null} */
  #onOpenEvent = null;

  /** @type {((event: Event) => void) | null} */
  #onEditorSelect = null;

  /** @type {((event: Event) => void) | null} */
  #onEditorDeselect = null;

  /** @type {number | undefined} */
  #closeTimer = undefined;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    // The header renders one of these on every page. If a second ever appears, only
    // the first answers triggers — otherwise every popup on the page would open at
    // once, stacked on top of each other.
    if (document.querySelector('hidden-ticket-popup') !== this) return;

    this.#onDocumentClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest(CLOSE_SELECTOR) && this.contains(target)) {
        event.preventDefault();
        this.close();
        return;
      }

      // A ticket was chosen. Get out of the way: the booking app opens its calendar as
      // an ordinary overlay, and a dialog in the top layer sits above every z-index, so
      // leaving ours open buries theirs behind our backdrop.
      // Deliberately no preventDefault/stopPropagation — the app's own click handler
      // still has to run. Closing is deferred a frame so the link's default action and
      // that handler both see the DOM intact, and skips the slide-out so their calendar
      // isn't behind ours even briefly.
      if (target.closest('.hidden-ticket-option') && this.contains(target)) {
        requestAnimationFrame(() => this.#closeNow());
        return;
      }

      const trigger = target.closest(OPEN_SELECTOR);
      if (!trigger) return;

      // Only a popup with no rows at all defers to the trigger's own link. Rows that
      // exist but have no booking link yet still open the chooser — they render
      // visibly inactive, which is the honest state while the events are being set
      // up, and it means the chooser can be seen and tested before the URLs exist.
      if (!this.#hasOptions()) return;

      event.preventDefault();
      // The CTA this replaces is the one Easy Appointment Booking already binds its
      // own modal to. Listening in the capture phase and stopping the click here
      // means the app's handler never runs, so the customer gets this chooser
      // instead of both popups at once.
      event.stopPropagation();
      this.open();
    };
    document.addEventListener('click', this.#onDocumentClick, { capture: true });

    this.#onOpenEvent = () => this.open();
    document.addEventListener(OPEN_EVENT, this.#onOpenEvent);

    // Clicking the dialog element itself means the backdrop: the content fills the
    // dialog's box, so any hit on the element proper is outside the content.
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.close();
    });

    // Esc fires `cancel` before `close`; let the animation run instead of snapping shut.
    this.#dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });

    // Theme editor: selecting the block (or one of its rows) should reveal it.
    this.#onEditorSelect = (event) => {
      if (event.target instanceof Node && this.contains(event.target)) this.open();
    };
    this.#onEditorDeselect = (event) => {
      if (event.target instanceof Node && this.contains(event.target)) this.close();
    };
    document.addEventListener('shopify:block:select', this.#onEditorSelect);
    document.addEventListener('shopify:block:deselect', this.#onEditorDeselect);
  }

  disconnectedCallback() {
    if (this.#onDocumentClick) document.removeEventListener('click', this.#onDocumentClick, { capture: true });
    if (this.#onOpenEvent) document.removeEventListener(OPEN_EVENT, this.#onOpenEvent);
    if (this.#onEditorSelect) document.removeEventListener('shopify:block:select', this.#onEditorSelect);
    if (this.#onEditorDeselect) document.removeEventListener('shopify:block:deselect', this.#onEditorDeselect);
    this.#onDocumentClick = null;
    this.#onOpenEvent = null;
    this.#onEditorSelect = null;
    this.#onEditorDeselect = null;
    clearTimeout(this.#closeTimer);
  }

  open() {
    if (!this.#dialog || this.#dialog.open) return;
    clearTimeout(this.#closeTimer);
    this.#dialog.removeAttribute('data-closing');
    this.#dialog.showModal();
    // showModal() focuses the first focusable descendant — the ✕ — which then paints
    // a focus ring the moment the popup opens, as if it were already pressed. The
    // `autofocus` attribute can't redirect this: the spec looks for a descendant
    // carrying it, and the dialog is not its own descendant. So move focus explicitly
    // to the dialog (tabindex="-1" makes it focusable). Screen readers still announce
    // the dialog and its title; ✕ and the rows keep their normal Tab order.
    this.#dialog.focus();
    this.#setScrollLocked(true);
  }

  close() {
    const dialog = this.#dialog;
    if (!dialog || !dialog.open || dialog.hasAttribute('data-closing')) return;

    dialog.setAttribute('data-closing', '');

    const finish = () => {
      clearTimeout(this.#closeTimer);
      dialog.removeEventListener('animationend', onAnimationEnd);
      dialog.removeAttribute('data-closing');
      if (dialog.open) dialog.close();
      this.#setScrollLocked(false);
    };

    /** @param {AnimationEvent} event */
    const onAnimationEnd = (event) => {
      // Only the dialog's own slide-out counts — not anything bubbling from a row.
      if (event.target === dialog) finish();
    };

    // Reduced motion suppresses the slide-out, so `animationend` never arrives.
    // Close straight away rather than sitting through the fallback timer.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }

    dialog.addEventListener('animationend', onAnimationEnd);
    this.#closeTimer = window.setTimeout(finish, CLOSE_ANIMATION_FALLBACK_MS);
  }

  /** Shut immediately, no slide-out — used when handing off to the booking app. */
  #closeNow() {
    clearTimeout(this.#closeTimer);
    this.#dialog?.removeAttribute('data-closing');
    if (this.#dialog?.open) this.#dialog.close();
    this.#setScrollLocked(false);
  }

  /**
   * Core's <dialog scroll-lock> sets html[scroll-lock] { overflow: hidden }, which is
   * enough for native scrolling — but this theme runs Lenis, which drives the scroll
   * position from JS and sails straight past `overflow: hidden`. Pause the instance
   * too (hidden-motion.js exposes it), or the page keeps scrolling behind the popup.
   * Absent under reduced motion and in the theme editor, hence the optional call.
   * @param {boolean} locked
   */
  #setScrollLocked(locked) {
    const lenis = globalThis.__hiddenLenis;
    if (!lenis) return;
    if (locked) lenis.stop();
    else lenis.start();
  }

  /**
   * Any row counts, linked or not — an unlinked row shows as inactive rather than
   * being a reason to suppress the whole chooser. Only a completely empty popup hands
   * the click back to the trigger.
   * @returns {boolean} whether there is any ticket row to show.
   */
  #hasOptions() {
    return Boolean(this.querySelector('.hidden-ticket-option'));
  }
}

if (!customElements.get('hidden-ticket-popup')) {
  customElements.define('hidden-ticket-popup', HiddenTicketPopup);
}
